<!--
  NetItem
  -------
  Provides a wrapper for items in the net.
  Makes them selectable, draggable, resizable and clickable.
-->

<template>
  <div
    v-bind="functionalAttrs"
    class="net-item"
    :style="{
      ...position
    }"
    :class="[{ '-unclickable': !clickable || disabled, ...styleAttrs.class }, `net-item--${uid}`]"
    :data-uid="uid"
    :data-node-uid="nodeUid"
    @contextmenu="onContextMenu"
    @pointerover="onMouseOver"
    @pointerout="onMouseOut"
  >
    <slot name="before"></slot>

    <article
      ref="el"
      v-if="size"
      class="net-item__main"
      :class="[
        {
          'is-selected': selected,
          'is-highlighted': highlighted,
          'is-faded': faded,
          'is-faded-out': faded && fade,
          'is-fixed': fixed,
          '-focussed': focussed,
          '-disabled': disabled,
        },
        itemClass
      ]"
      :style="[
        sizeStyles,
        itemStyle
      ]"
      @pointerdown.exact="onMouseDown"
      @pointerdown.shift="onShiftMouseDown"
      @pointerup.exact="onMouseUp"
    >
      <slot></slot>
    </article>

    <slot name="after"></slot>
  </div>
</template>

<script setup lang="ts" generic="T extends string">
import { computed, reactive, ref, toRef } from "vue";
import { useNetDrag } from "./useNetDrag";
import { useAttrsAndStyle } from "@core/composables/attrsAndStyle";

import type { StyleValue } from "vue";
import type { HandleableMouseEvent } from "@core/types/Events";
import type { Uid } from "@/helpers/utility/Id";
import type { RotatableRect } from "@editor/items/scene/SceneNodeItem";

defineOptions({
  name: "NetItem"
});

const props = withDefaults(defineProps<{
  uid: Uid<T>;
  size: RotatableRect;
  nodeUid?: Uid<"sceneNode">;
  moveable?: boolean;
  resizable?: boolean;
  minWidth?: number;
  minHeight?: number;
  itemClass?: string;
  itemStyle?: StyleValue;
  focussed?: boolean;
  selected?: boolean;
  highlighted?: boolean;
  faded?: boolean;
  fade?: boolean;
  disabled?: boolean;
  clickable?: boolean;
  fixed?: boolean;
  locked?: boolean;
  gridSize?: number;
  aspectRatioFixed?: boolean;
  edges?: {
    top: boolean;
    right: boolean;
    bottom: boolean;
    left: boolean;
  };
}>(), {
  moveable: false,
  resizable: false,
  minWidth: 200,
  minHeight: 80,
  itemClass: "",
  itemStyle: () => ({}),
  focussed: false,
  selected: false,
  highlighted: false,
  faded: false,
  fade: false,
  disabled: false,
  fixed: false,
  locked: false,
  clickable: true,
  gridSize: 10,
  aspectRatioFixed: false,
  edges: () => ({
    top: false,
    right: true,
    bottom: true,
    left: true
  })
});

const emit = defineEmits<{
  (e: "drag-start"): void;
  (e: "drag-end"): void;
  (e: "move", delta: { x: number; y: number; gridX: number; gridY: number }, totalDelta: { x: number, y: number }): void;
  (
    e: "resize",
    newSize: { x: number; y: number; width: number; height: number }
  ): void;
  (e: "update:sizes"): void;
  (e: "hoveroff"): void;
  (e: "hoveron"): void;
  (e: "select", item: { uid: Uid<T> }): void;
  (e: "add-select", item: { uid: Uid<T> }): void;
  (e: "contextmenu", mouseEvent: MouseEvent & { item?: { uid: Uid<T> } }): void;
}>();

const el = ref<HTMLElement | null>(null);

const state = reactive({
  mouseDown: false,
  hover: false
});

useNetDrag(el, {
  gridSize: toRef(props, "gridSize"),

  size: computed(() => ({
    x: props.size.x,
    y: props.size.y,
    width: props.size.width,
    height: props.size.height ?? props.minHeight,
  })),
  minWidth: toRef(props, "minWidth"),
  minHeight: toRef(props, "minHeight"),

  draggable: toRef(props, "moveable"),
  resizable: toRef(props, "resizable"),

  aspectRatioFixed: toRef(props, "aspectRatioFixed"),

  edges: toRef(props, "edges"),

  onDragStart() {
    // Group the action to make sure the movement of all
    // elements can be undone at once
    emit("drag-start");
  },

  /**
   * changes the x and y positions of currently selected elements
   */
  onDragMove(event, coords, delta, totalDelta) {
    emit("move", delta, totalDelta);
  },

  onDragEnd() {
    emit("update:sizes");
    emit("drag-end");
  },

  onResize(newSize) {
    emit("resize", newSize);
  },

  onResizeEnd() {
    emit("update:sizes");
  }
});

const sizeStyles = computed(() => {
  return {
    width: `${props.size.width}px`,
    height: `${props.size.height}px`
  };
  // const styles: { width?: string; height?: string } = {};
  // if (props.size.width != null) {
  //   styles.width = `${props.size.width}px`;
  // }
  // if (props.size.height != null) {
  //   styles.height = `${props.size.height}px`;
  // }
  // return styles;
});

const position = computed(() => {
  if (!props.size) return {};
  return {
    left: `${props.size.x}px`,
    top: `${props.size.y}px`,
    rotate: props.size.rotation ? `${props.size.rotation}deg` : undefined
  };
});

const { functionalAttrs, styleAttrs } = useAttrsAndStyle();

function onMouseDown(mouseEvent: HandleableMouseEvent) {
  if (props.disabled) return;
  if (mouseEvent.hasBeenHandled) {
    console.log("Not selecting, mouse event has been handled already.", mouseEvent.handledBy);
    return;
  }

  const MIDDLE_MOUSE_BUTTON = 1;
  const RIGHT_MOUSE_BUTTON = 2;
  if (mouseEvent.button === MIDDLE_MOUSE_BUTTON) {
    return;
  }
  if (mouseEvent.button === RIGHT_MOUSE_BUTTON) {
    mouseEvent.hasBeenHandled = true;
    mouseEvent.handledBy = "NetItem";
    return;
  }

  mouseEvent.hasBeenHandled = true;
  mouseEvent.handledBy = "NetItem";
  state.mouseDown = true;
  select(props.uid);
}

function onShiftMouseDown(mouseEvent: HandleableMouseEvent) {
  if (props.disabled) return;

  // don't handle middle mouse key.
  if (mouseEvent.which === 2) {
    return;
  }
  mouseEvent.hasBeenHandled = true;
  mouseEvent.handledBy = "NetItem";
  mouseEvent.preventDefault();
  emit("add-select", { uid: props.uid });
}

function onMouseUp(mouseEvent: MouseEvent) {
  if (state.mouseDown === true && !props.disabled) {
    onClick(mouseEvent);
  }
  state.mouseDown = false;
}

function onClick(mouseEvent: HandleableMouseEvent) {
  if (mouseEvent.hasBeenHandled) return;
  mouseEvent.hasBeenHandled = true;
  mouseEvent.handledBy = "NetItem";
}

function onMouseOver(mouseEvent: HandleableMouseEvent) {
  if (props.disabled) return;
  if (mouseEvent.hasBeenHandled) return;
  mouseEvent.hasBeenHandled = true;
  mouseEvent.handledBy = "NetItem";
  state.hover = true;
  emit("hoveron");
}
function onMouseOut() {
  state.hover = false;
  emit("hoveroff");
}

function select(uid: Uid<T>) {
  if (props.disabled) return;
  emit("select", { uid });
}

function onContextMenu(mouseEvent: MouseEvent & { item?: { uid: Uid<T> } }) {
  if (props.disabled) return;
  mouseEvent.preventDefault();
  mouseEvent.stopPropagation();
  mouseEvent.item = { uid: props.uid };
  emit("contextmenu", mouseEvent);
}
</script>

<style lang="scss">
.net-item {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: auto;
  transform-origin: center;

  &.-unclickable,
  &.-unclickable * {
    pointer-events: none !important;
  }
}
</style>
